<div
  class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-ink/80 px-4 py-10"
  (click)="close()"
>
  <div
    class="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-md border border-hairline bg-panel p-6"
    (click)="$event.stopPropagation()"
  >
    <div class="mb-4 flex items-start justify-between gap-4">
      <h2 class="font-display text-xl font-semibold text-parchment">Bulk add people</h2>
      <button
        type="button"
        (click)="close()"
        class="text-xl leading-none text-dusk hover:text-parchment"
        aria-label="Close"
      >
        &times;
      </button>
    </div>

    @if (step() === 'input') {
      <p class="mb-4 text-sm text-dusk">
        Paste a comma-separated list of names. Anything already in the catalog (alive or
        deceased) is skipped — you'll only see the ones that need adding.
      </p>
      <form [formGroup]="namesForm" (ngSubmit)="lookUp()" class="flex flex-col gap-3">
        <textarea
          formControlName="names"
          rows="6"
          placeholder="Freddie Mercury, Betty White, ..."
          class="rounded border border-hairline bg-ink px-3 py-2 text-parchment placeholder:text-dusk/60 focus:border-gold focus:outline-none"
        ></textarea>
        <button
          type="submit"
          [disabled]="lookingUp()"
          class="inline-flex shrink-0 items-center justify-center self-start rounded-[3px] bg-gradient-to-b from-gold to-gold-dim px-5 py-2.5 text-sm font-bold text-ink transition hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
        >
          {{ lookingUp() ? 'Looking up…' : 'Look up' }}
        </button>
      </form>

      @if (lookingUp()) {
        <div class="mt-3">
          <p class="mb-1 text-xs text-dusk">
            Looking up {{ processedCount() }} of {{ totalToProcess() }}…
          </p>
          <div class="h-1.5 w-full overflow-hidden rounded-full bg-ink">
            <div
              class="h-full rounded-full bg-gradient-to-r from-gold to-gold-dim transition-[width]"
              [style.width.%]="progressPercent()"
            ></div>
          </div>
        </div>
      }

      @if (lookupError()) {
        <p class="mt-3 rounded border border-wax/40 bg-wax/10 px-4 py-3 text-sm text-parchment">
          {{ lookupError() }}
        </p>
      }
    }

    @if (step() === 'results') {
      <p class="mb-4 text-sm text-dusk">
        {{ alreadyCatalogedCount() }} of {{ inputCount() }} already in the catalog.
        @if (rows().length > 0) {
          Showing the {{ rows().length }} that need review.
        }
      </p>

      @if (rows().length === 0) {
        <p class="text-sm text-dusk">Everything you pasted is already in the catalog.</p>
      } @else {
        <div class="flex flex-col gap-2 overflow-y-auto">
          @for (row of rows(); track row.name) {
            <div class="rounded border border-hairline p-3">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <p class="font-medium text-parchment">{{ row.name }}</p>
                  <p class="mt-1 text-xs uppercase tracking-wide text-dusk">
                    @if (row.status === 'added') {
                      Added
                    } @else if (row.visible.length === 1) {
                      Found on Wikipedia
                    } @else if (row.visible.length > 1) {
                      {{ row.visible.length }} possible matches
                    } @else if (row.hidden.length > 0) {
                      No confirmed match
                    } @else {
                      Not found on Wikipedia
                    }
                  </p>
                  @if (row.status === 'error') {
                    <p class="mt-1 text-xs text-wax-bright">{{ row.errorMessage }}</p>
                  }
                </div>

                @if (row.status === 'added') {
                  <span class="shrink-0 text-sm font-semibold text-gold">✓ Added</span>
                } @else {
                  <button
                    type="button"
                    (click)="onAddClick(row)"
                    [disabled]="
                      row.status === 'adding' || (row.visible.length === 0 && row.hidden.length === 0)
                    "
                    class="inline-flex shrink-0 items-center justify-center rounded-[3px] border border-hairline px-4 py-1.5 text-sm font-semibold text-parchment transition hover:border-gold hover:text-gold disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {{ row.status === 'adding' ? 'Adding…' : 'Add' }}
                  </button>
                }
              </div>

              @if (row.status === 'expanded') {
                <ng-template #candidateRow let-candidate>
                  <button
                    type="button"
                    (click)="pickCandidate(row, candidate)"
                    class="flex w-full items-center gap-3 rounded p-2 text-left transition hover:bg-parchment/[0.04]"
                  >
                    @if (candidate.imageUrl) {
                      <img
                        [src]="candidate.imageUrl"
                        alt=""
                        class="h-10 w-10 shrink-0 rounded-full object-cover"
                      />
                    }
                    <div>
                      <p class="text-sm text-parchment">
                        {{ candidate.name }}{{ candidate.aliases?.length ? ' "' + candidate.aliases.join('", "') + '"' : '' }}{{ candidate.knownFor ? ' (' + candidate.knownFor + ')' : '' }}
                      </p>
                      @if (candidate.dateOfBirth === null) {
                        <p class="text-xs text-dusk">no confirmed birth date</p>
                      }
                    </div>
                  </button>
                </ng-template>

                <div class="mt-2 flex flex-col gap-1 border-t border-hairline pt-2">
                  @for (candidate of row.visible; track candidateKey(candidate)) {
                    <ng-container [ngTemplateOutlet]="candidateRow" [ngTemplateOutletContext]="{ $implicit: candidate }" />
                  }

                  @if (row.hidden.length > 0) {
                    <button
                      type="button"
                      (click)="toggleHidden(row)"
                      class="rounded px-2 py-1 text-left text-xs text-dusk transition hover:bg-parchment/[0.04] hover:text-parchment hover:underline"
                    >
                      {{ row.showHidden ? 'Hide' : 'Show ' + row.hidden.length + ' Hidden' }}
                    </button>
                    @if (row.showHidden) {
                      @for (candidate of row.hidden; track candidateKey(candidate)) {
                        <ng-container [ngTemplateOutlet]="candidateRow" [ngTemplateOutletContext]="{ $implicit: candidate }" />
                      }
                    }
                  }

                  <button
                    type="button"
                    (click)="collapseRow(row)"
                    class="self-start text-xs text-dusk hover:text-parchment hover:underline"
                  >
                    Cancel
                  </button>
                </div>
              }
            </div>
          }
        </div>
      }
    }
  </div>
</div>
